import React, { useEffect, useState } from 'react';
import './Admin.css';

const AdminPanel = () => {
  const [socialImages, setSocialImages] = useState([]);
  const [institutionImages, setInstitutionImages] = useState([]);
  const [videoLinks, setVideoLinks] = useState([]);
  const [newVideo, setNewVideo] = useState({ link: '', description: '' });

  const [showSocial, setShowSocial] = useState(true);
  const [showInstitution, setShowInstitution] = useState(true);
  const [dragOverSocial, setDragOverSocial] = useState(false);
  const [dragOverInstitution, setDragOverInstitution] = useState(false);

  const BASE_URL = 'https://gainwissdom.onrender.com';

  const uploadToServer = async (files, category) => {
    for (const file of files) {
      const formData = new FormData();
      formData.append('image', file);

      try {
        const res = await fetch(`${BASE_URL}/api/upload?category=${category}`, {
          method: 'POST',
          body: formData,
        });

        const data = await res.json();
        if (res.ok) {
          const imageUrl = data.imageUrl;
          if (category === 'social') {
            setSocialImages((prev) => [...prev, imageUrl]);
          } else {
            setInstitutionImages((prev) => [...prev, imageUrl]);
          }
        } else {
          alert(data.error || 'Upload failed');
        }
      } catch (err) {
        console.error('Upload error:', err);
        alert('Error uploading file');
      }
    }
  };

  const handleFileChange = (e, category) => {
    const files = Array.from(e.target.files);
    uploadToServer(files, category);
  };

  const addVideo = async () => {
    const { link, description } = newVideo;
    if (!link.trim() || !description.trim()) {
      return alert('Enter both link and description');
    }

    if (!link.includes('https://www.youtube.com/embed/')) {
      return alert('Use an embed link (https://www.youtube.com/embed/...)');
    }

    try {
      const res = await fetch(`${BASE_URL}/api/videos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ link: link.trim(), description: description.trim() }),
      });

      const data = await res.json();
      if (res.ok) {
        setVideoLinks((prev) => [...prev, { link, description }]);
        setNewVideo({ link: '', description: '' });
        alert('✅ Video added successfully');
      } else {
        alert(data.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Video upload error:', err);
      alert('Error uploading video');
    }
  };

  const deleteImage = async (filename, category) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this image?');
    if (!confirmDelete) return;

    try {
      const res = await fetch(
        `${BASE_URL}/api/upload/delete?category=${category}&filename=${encodeURIComponent(filename)}`,
        { method: 'DELETE' }
      );

      const data = await res.json();
      if (res.ok) {
        if (category === 'social') {
          setSocialImages(prev => prev.filter(url => !url.includes(filename)));
        } else {
          setInstitutionImages(prev => prev.filter(url => !url.includes(filename)));
        }
        alert('🗑️ Image deleted');
      } else {
        alert(data.error || 'Delete failed');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Error deleting image');
    }
  };

  const deleteVideo = async (link) => {
    const confirmDelete = window.confirm('Are you sure you want to delete this video?');
    if (!confirmDelete) return;

    try {
      const res = await fetch(`${BASE_URL}/api/videos/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ link }),
      });

      const data = await res.json();
      if (res.ok) {
        setVideoLinks(prev => prev.filter((video) => video.link !== link));
        alert('🎬 Video deleted');
      } else {
        alert(data.error || 'Delete failed');
      }
    } catch (err) {
      console.error('Delete error:', err);
      alert('Error deleting video');
    }
  };

  // ✅ FIXED useEffect to use file.filename properly
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/upload/list`);
        const data = await res.json();

        if (res.ok) {
          const social = data.social.map(file => `${BASE_URL}/uploads/social/${file.filename}`);
          const institution = data.institution.map(file => `${BASE_URL}/uploads/institution/${file.filename}`);
          setSocialImages(social);
          setInstitutionImages(institution);
        }
      } catch (err) {
        console.error('Image fetch error:', err);
      }

      try {
        const res = await fetch(`${BASE_URL}/api/videos`);
        const videoData = await res.json();
        if (res.ok) {
          setVideoLinks(videoData);
        }
      } catch (err) {
        console.error('Video fetch error:', err);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="admin-panel">
      <h2>Admin Dashboard</h2>

      {/* Social Images */}
      <div className="upload-section">
        <h3>
          📸 Social Media Images
          <button className="dropdown-btn" onClick={() => setShowSocial(!showSocial)}>
            {showSocial ? '▲ Hide' : '▼ Show'}
          </button>
        </h3>
        {showSocial && (
          <>
            <div
              className={`drop-zone ${dragOverSocial ? 'drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOverSocial(true); }}
              onDragLeave={() => setDragOverSocial(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverSocial(false);
                handleFileChange({ target: { files: e.dataTransfer.files } }, 'social');
              }}
            >
              Drag & Drop images or choose file
              <input type="file" multiple accept="image/*" onChange={(e) => handleFileChange(e, 'social')} />
            </div>
            <div className="preview-grid">
              {socialImages.map((img, idx) => {
                const filename = img.split('/').pop();
                return (
                  <div key={idx} className="preview-item">
                    <img src={img} alt={`social-${idx}`} />
                    <button onClick={() => deleteImage(filename, 'social')}>🗑️ Delete</button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Institution Images */}
      <div className="upload-section">
        <h3>
          🏫 Institution Images
          <button className="dropdown-btn" onClick={() => setShowInstitution(!showInstitution)}>
            {showInstitution ? '▲ Hide' : '▼ Show'}
          </button>
        </h3>
        {showInstitution && (
          <>
            <div
              className={`drop-zone ${dragOverInstitution ? 'drag-over' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragOverInstitution(true); }}
              onDragLeave={() => setDragOverInstitution(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverInstitution(false);
                handleFileChange({ target: { files: e.dataTransfer.files } }, 'institution');
              }}
            >
              Drag & Drop images or choose file
              <input type="file" multiple accept="image/*" onChange={(e) => handleFileChange(e, 'institution')} />
            </div>
            <div className="preview-grid">
              {institutionImages.map((img, idx) => {
                const filename = img.split('/').pop();
                return (
                  <div key={idx} className="preview-item">
                    <img src={img} alt={`institution-${idx}`} />
                    <button onClick={() => deleteImage(filename, 'institution')}>🗑️ Delete</button>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {/* Videos */}
      <div className="upload-section">
        <h3>🎥 Add YouTube Video</h3>
        <input
          type="text"
          placeholder="Embed link (https://www.youtube.com/embed/...)"
          value={newVideo.link}
          onChange={(e) => setNewVideo({ ...newVideo, link: e.target.value })}
        />
        <input
          type="text"
          placeholder="Description"
          value={newVideo.description}
          onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
        />
        <button onClick={addVideo}>Add Video</button>

        <div className="preview-grid">
          {videoLinks.map((video, idx) => (
            <div key={idx} className="preview-item">
              <iframe src={video.link} title={`video-${idx}`} allowFullScreen></iframe>
              <p className="video-caption">{video.description}</p>
              <button onClick={() => deleteVideo(video.link)}>🗑️ Delete</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;
